import styles from "./UserInfo.module.css";
import { UserInfo as UserInfoType } from "../common/Common";
import Button from "../common/Button";
import ContentTitle from "../common/ContentTitle";
import UserInfoItem from "./UserInfoItem";
import { MembershipInfo } from "./UserInfoArea";
import ToggleButton from "../common/ToggleButton";
import { MouseEvent, useRef, useState } from "react";
import UserInfoInput from "./UserInfoInput";
import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import DataProvider from "../../data_provider/DataProvider";

interface UserAreaProps {
    data: UserInfoType;
    membershipInfo: MembershipInfo | undefined;
    handleEditMode: (mode: string) => void;
    editUserInfo: (formData: FormData) => void;
}
/**
 * @author bakim
 * @date 2024-09-11 10:56:38
 * @remark 내 정보 페이지
 * @param {UserInfoType} data (required) 사용자 정보
 * @param {MembershipInfo | undefined} membershipInfo (required) 사용자 멤버십 가입 정보
 * @param {(mode: string) => void} handleEditMode (required) 사용자 정보 수정 모드 컨트롤 함수
 * @param {(formData: FormData) => void} editUserInfo (required) 사용자 정보 변경 요청 함수
 * @returns UserInfo
 */
const UserInfo = (props: UserAreaProps) => {
    const data = { ...props.data };
    const editFormRef = useRef<HTMLFormElement>(null);
    const grade: string = data.membership ? data.membership.grade : "일반사용자";
    const [editInfoType, setEditInfoType] = useState<boolean>(false);

    const {ModalComponent, openModal, closeModal} = useModal();

    /**
     *  탈퇴 버튼 콜백 함수
     */
    const onClickWithdraw = (e: MouseEvent<HTMLElement>) => {
        props.handleEditMode("withdraw");
    };

    /**
     *  비밀번호 수정 버튼 콜백 함수
     */
    const onClickPasswordEdit = (e: MouseEvent<HTMLElement>) => {
        props.handleEditMode("password");
    };

    /**
     *  사용자 별명 수정 버튼 콜백 함수
     */
    const onClickUserNameEdit = () => {
        setEditInfoType(!editInfoType);
        editInfoType && editFormRef.current && props.editUserInfo(new FormData(editFormRef.current));
    };

     /**
     *  구독취소 버튼 콜백 함수
     */
    const onClickUnsubscribe = (e: MouseEvent<HTMLElement>) => {
        openModal({
            title: "안내",
            children: "정말 구독을 취소하시겠습니까?",
            modalButtons: (
                <ModalButtons
                    buttonCount={2}
                    buttons={[{
                        label: "취소",
                        color: "Secondary",
                        onClick: () => {
                            closeModal();
                        }
                    },
                    {
                        label: "구독 취소",
                        color: "Danger",
                        onClick: () => {
                            closeModal();
                            submitUnsubscribe();
                        }
                    }]} />
            )
        });
    };

    const submitUnsubscribe = async () => {
        const result = await DataProvider.Unsubscribe();
        if (result.flag) {
            openModal({
                title: "구독 취소가 완료되었습니다.",
                children: (
                    <>
                        다음 결제일부터 요금이 청구되지 않습니다.
                        <br />
                        더 나은 서비스를 위해 항상 노력하겠습니다.
                    </>
                ),
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => {
                                    closeModal();
                                    window.location.reload();
                                },
                            },
                        ]}
                    />
                ),
            });
        } else {
            openModal({
                title: "안내",
                children: (
                    <>
                        구독 취소에 실패하였습니다.
                        <br />
                        다시 시도해주세요.
                    </>
                ),
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => {
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
            });
        }
    };


    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <ContentTitle title="로그인 정보" />
                <UserInfoItem id="userid" label="아이디" value={data.userId} />

                {editInfoType ? (
                    <form ref={editFormRef}>
                        <UserInfoInput label="별명" name="username" button={"완료"} value={data.username} placeholder={data.username} onButtonClick={onClickUserNameEdit} />
                    </form>
                ) : (
                    <UserInfoItem id="username" label="별명" value={data.username} button={"수정하기"} buttonOnClick={onClickUserNameEdit} />
                )}

                {data.platform == 1 ? (<UserInfoItem id="password" label="비밀번호" value="" button="수정하기" buttonOnClick={onClickPasswordEdit} />) : ""}
            </div>
            {!data.admin && (
                <>
                    {props.membershipInfo && (
                        <div className={styles.content}>
                            <ContentTitle title="멤버십" />
                            <div className={styles.horizontal}>
                                <UserInfoItem id="grade" label="회원 등급" value={`모두의지도 ${grade}`}/>
                                {props.membershipInfo.subscribe && 
                                    <Button label="구독&nbsp;취소하기" size="small" onClick={onClickUnsubscribe} />
                                }
                            </div>
                            <UserInfoItem id="grade-desc" value={props.membershipInfo.msg} />
                            {props.membershipInfo.label && (
                                <UserInfoItem
                                    id="grade-setting"
                                    value=""
                                    button={props.membershipInfo.label}
                                    vertical={true}
                                    buttonOnClick={props.membershipInfo.callback}
                                />
                            )}
                        </div>
                    )}
                    <div className={styles.content}>
                        <ContentTitle title="사용자 수신 동의" />
                        <div className={styles.horizontal}>
                            <UserInfoItem
                                id="mail-receive-agreement"
                                label="메일 수신 동의"
                                labelDesc="모두의지도에서 보내주는 알림 지도를 받아볼 수 있어요."
                                value=""
                            />
                            <ToggleButton onToggle={props.editUserInfo} isOn={Boolean(props.data.emailNotification)} />
                        </div>
                    </div>
                    <div className={styles.withdraw} onClick={onClickWithdraw}>
                        <span>탈퇴하기</span>
                    </div>
                </>
            )}
            <ModalComponent />
        </div>
    );
};

export default UserInfo;
